import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { receivedDemandsService } from '../../../services/receivedDemands';
import type { Demanda } from '../../../models';
import { findRelatedDemandas } from '../utils/relatedDemandas';
import { useEventListener } from '../../../hooks/useEventBus';

interface UseDemandNavigationProps {
  currentDemanda: Demanda | null;
  demandType?: string;
  onDemandSelect?: (demanda: Demanda) => void;
}

export type DemandListType = 'all' |'received' | 'sent' | 'watching' | 'toAssign' ;

export const useDemandNavigation = ({ 
  currentDemanda, 
  demandType = 'all',
  onDemandSelect
}: UseDemandNavigationProps) => {
  const navigate = useNavigate();
  const availableListTypes: DemandListType[] = useMemo(
    () => ['all', 'received', 'toAssign', 'sent', 'watching'],
    []
  );
  
  // Estados para todas as demandas e demandas relacionadas
  const [allDemandas, setAllDemandas] = useState<Demanda[]>([]);
  const [relatedDemandas, setRelatedDemandas] = useState<Demanda[]>([]);
  const [loadingAllDemandas, setLoadingAllDemandas] = useState(false);
  
  // Controle de requisições para evitar duplicações
  const fetchingRef = useRef<boolean>(false);
  const lastFetchedTypeRef = useRef<DemandListType | null>(null);
  
  const normalizedInitialListType: DemandListType = useMemo(() => {
    const candidate = demandType as DemandListType;
    return availableListTypes.includes(candidate) ? candidate : 'all';
  }, [availableListTypes, demandType]);
  
  const [listType, setListType] = useState<DemandListType>(normalizedInitialListType);

  useEffect(() => {
    setListType(normalizedInitialListType);
  }, [normalizedInitialListType]);

  const fetchCombinedDemandas = useCallback(async () => {
    const [received, toAssign, sent, watching] = await Promise.all([
      receivedDemandsService.getDemandasRecebidas(),
      receivedDemandsService.getDemandasEmAtribuicao(),
      receivedDemandsService.getDemandasEnviadas(),
      receivedDemandsService.getDemandasObservaveis(),
    ]);

    const uniqueDemandas = new Map<number, Demanda>();

    const appendList = (demandas: Demanda[]) => {
      demandas.forEach((demanda) => {
        if (typeof demanda.id !== 'number') {
          return;
        }

        if (!uniqueDemandas.has(demanda.id)) {
          uniqueDemandas.set(demanda.id, demanda);
        }
      });
    };

    appendList(received);
    appendList(toAssign);
    appendList(sent);
    appendList(watching);

    return Array.from(uniqueDemandas.values());
  }, []);

  const resolveDemandasByType = useCallback(async (type: DemandListType) => {
    switch (type) {
      case 'received':
        return receivedDemandsService.getDemandasRecebidas();
      case 'toAssign':
        return receivedDemandsService.getDemandasEmAtribuicao();
      case 'sent':
        return receivedDemandsService.getDemandasEnviadas();
      case 'watching':
        return receivedDemandsService.getDemandasObservaveis();
      case 'all':
      default:
        return fetchCombinedDemandas();
    }
  }, [fetchCombinedDemandas]);
  
  // Buscar todas as demandas para encontrar relacionadas
  const fetchAllDemandas = useCallback(async (type: DemandListType, force: boolean = false) => {
    // Evitar requisições duplicadas
    if (!force && (fetchingRef.current || lastFetchedTypeRef.current === type)) {
      return;
    }

    try {
      fetchingRef.current = true;
      setLoadingAllDemandas(true);
      const todasDemandas = await resolveDemandasByType(type);
      setAllDemandas(todasDemandas);
      lastFetchedTypeRef.current = type;
    } catch (error) {
      console.error('Erro ao buscar todas as demandas:', error);
      lastFetchedTypeRef.current = null;
    } finally {
      setLoadingAllDemandas(false);
      fetchingRef.current = false;
    }
  }, [resolveDemandasByType]);

  // Atualizar demandas relacionadas quando a demanda atual ou todas as demandas mudarem
  useEffect(() => {
    if (currentDemanda && allDemandas.length > 0) {
      const related = findRelatedDemandas(currentDemanda, allDemandas, 8);
      setRelatedDemandas(related);
    } else {
      setRelatedDemandas([]);
    }
  }, [currentDemanda, allDemandas]);

  // Buscar demandas quando o tipo da lista mudar
  useEffect(() => {
    // Resetar o controle quando o tipo mudar
    if (lastFetchedTypeRef.current !== listType) {
      lastFetchedTypeRef.current = null;
    }
    fetchAllDemandas(listType);
  }, [fetchAllDemandas, listType]);

  // Navegação interna (sem recarregar página) - nova funcionalidade
  const selectDemandLocally = useCallback((demanda: Demanda) => {
    if (onDemandSelect) {
      onDemandSelect(demanda);
    }
  }, [onDemandSelect]);

  // Navegação para uma demanda específica (recarrega página)
  const navigateToDemand = useCallback((demanda: Demanda) => {
    navigate(`/demands/${demanda.id}/view?type=all`);
  }, [navigate]);

  // Visualização de demanda em nova aba
  const viewDemandInNewTab = useCallback((demanda: Demanda) => {
    const url = `/demands/${demanda.id}/view?type=all`;
    window.open(url, '_blank');
  }, []);

  // Função para recarregar as demandas relacionadas
  const refreshRelatedDemandas = useCallback(async () => {
    lastFetchedTypeRef.current = null;
    await fetchAllDemandas(listType, true);
  }, [fetchAllDemandas, listType]);

  // Event listeners para atualizar a lista quando mutations ocorrerem
  useEventListener('demand-status-changed', async ({ demandId, newStatus }) => {
    // Atualizar a demanda na lista local imediatamente (optimistic update)
    if (newStatus) {
      setAllDemandas(prev => 
        prev.map(d => {
          if (d.id === demandId) {
            return { ...d, status: newStatus as Demanda['status'] } as Demanda;
          }
          return d;
        })
      );
    }
    
    // Refetch completo para garantir dados atualizados do servidor
    setTimeout(() => refreshRelatedDemandas(), 500);
  }, [refreshRelatedDemandas]);

  useEventListener('demand-updated', async () => {
    setTimeout(() => refreshRelatedDemandas(), 500);
  }, [refreshRelatedDemandas]);

  useEventListener('demand-assigned', async () => {
    setTimeout(() => refreshRelatedDemandas(), 500);
  }, [refreshRelatedDemandas]);

  const changeListType = useCallback((type: DemandListType) => {
    lastFetchedTypeRef.current = null; // Reset para forçar nova busca
    setListType(type);
  }, []);

  return {
    // Dados
    allDemandas,
    relatedDemandas,
    loadingAllDemandas,
    
    // Funções de navegação
    selectDemandLocally,
    navigateToDemand,
    viewDemandInNewTab,
    refreshRelatedDemandas,
    
    // Estado da demanda atual para o SideDemandTable
    selectedDemandId: currentDemanda?.id,
    listType,
    setListType: changeListType,
    availableListTypes,
  };
};